import { File } from 'lucide-react';
import SearchRangeWebIcon from '../Icons/SearchRangeWeb';
import { useRightDrawer } from '@/contexts/DrawerContext';
import { cn } from '@/lib/utils';
import { StarRating } from './LinkTooltip';
import { useTranslation } from '@/lib/i18n';

function getSourceUrl(source: any) {
  return source?.metadata?.url ?? source?.source_url ?? source?.url;
}

function getSourceTitle(source: any) {
  return source?.metadata?.title ?? source?.title ?? source?.source;
}

function getSourceDescription(source: any) {
  return source?.pageContent ?? source?.snippet ?? source?.publish_time;
}

const MessageSources = ({ sources }: { sources: any[] }) => {
  const { t } = useTranslation();
  const { openDrawer } = useRightDrawer();

  // 提取域名的辅助函数
  const extractDomain = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url;
    }
  };

  // 按域名去重，确保显示不同来源的favicon
  const uniqueDomainSources =
    sources?.reduce((acc: any[], item: any) => {
      const url = getSourceUrl(item);
      if (!url) return acc;

      const domain = extractDomain(url);
      const isDuplicate = acc.some((existingItem) => {
        const existingUrl = getSourceUrl(existingItem);
        return extractDomain(existingUrl) === domain;
      });

      if (!isDuplicate) {
        acc.push(item);
      }
      return acc;
    }, []) || [];

  return (
    <>
      <div className="flex flex-row items-center gap-2">
        <div
          className="border border-gray-200 dark:border-[#252525] pl-[2px] pr-2 rounded-[20px] h-[24px]  bg-bg-main flex flex-row gap-2 items-center cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() =>
            openDrawer(<SoruceTabPanel sources={sources} />, `${sources?.length} ${t('Sources')}`)
          }
        >
          <div className="flex flex-row items-center">
            {uniqueDomainSources.slice(0, 3).map((source, i) => {
              return (
                <div
                  className={`w-[17px] h-[17px] rounded-full border border-gray-100 dark:border-[#191B1D] ${i > 0 ? 'ml-[-6px]' : ''}`}
                  key={i}
                >
                  <img
                    src={`https://s2.googleusercontent.com/s2/favicons?domain_url=${getSourceUrl(source)}`}
                    width={16}
                    height={16}
                    alt="favicon"
                    className="rounded-full h-4 w-4  bg-bg-main"
                  />
                </div>
              );
            })}
          </div>
          <div className="text-text-secondary text-sm">
            {sources.length} {sources.length === 1 ? t('source') : t('sources')}
          </div>
        </div>
      </div>
    </>
  );
};

export default MessageSources;

export const SoruceTabPanel = ({ sources }: { sources: any }) => {
  const { t } = useTranslation();
  const sourceContent = sources?.map((item: any) => {
    return {
      url: getSourceUrl(item),
      title: getSourceTitle(item),
      desc: getSourceDescription(item),
    };
  });

  return (
    <div className="w-full flex flex-col">
      {/* Title Section */}
      <div className="flex items-center gap-3">
        <SearchRangeWebIcon className="text-text-secondary dark:text-[#FFF]" />
        <h3 className="text-text-primary font-medium text-base">{t('Web Search')}</h3>
      </div>
      <div className="w-full overflow-auto overflow-hidden-scrollable mt-3 flex flex-col items-center text-sm text-text-secondary gap-3">
        {sourceContent?.map((item: any, index: number) => (
          <SourceContentItem key={index} item={item} />
        ))}
      </div>
    </div>
  );
};

export function SourceContentItem({ item, classname }: { item: any; classname?: string }) {
  return (
    <a href={item?.url} target="_blank" className="w-full">
      <div
        className={cn(
          'w-full flex flex-col bg-[#f2f2f2] dark:bg-[#171717] px-3 py-4 gap-2.5 rounded-xl hover:bg-[#e9e9e9] dark:hover:bg-[#02020] transition-colors cursor-pointer',
          classname,
        )}
      >
        <div className="text-text-primary wrap-break-word text-[14px]">{item?.title}</div>
        <div className="wrap-break-word truncate-2-lines text-[12px] text-text-secondary dark:text-text-secondary">
          {item?.desc}
        </div>
        <div className="flex flex-row items-center gap-2">
          {item?.url === 'File' ? (
            <div className="bg-dark-200 hover:bg-dark-100 transition duration-200 flex items-center justify-center w-6 h-6 rounded-full">
              <File size={12} className="text-white/70" />
            </div>
          ) : (
            <div className="w-[17px] h-[17px] rounded-full border border-gray-300 dark:border-[#3E3E3E]">
              <img
                src={`https://s2.googleusercontent.com/s2/favicons?domain_url=${item?.url}`}
                width={16}
                height={16}
                alt="favicon"
                className="rounded-full h-4 w-4"
              />
            </div>
          )}
          <div>
            <p className="text-xs font-medium text-text-primary overflow-hidden whitespace-nowrap text-ellipsis">
              {item?.url?.replace(/.+\/\/|www.|\..+/g, '')}
            </p>
          </div>
          {/* 评分 */}
          {false && <StarRating />}
          {/* 信息图标 */}
          {false && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8C1.5 11.59 4.41 14.5 8 14.5C11.59 14.5 14.5 11.59 14.5 8C14.5 4.41 11.59 1.5 8 1.5ZM8.625 11.125H7.375V7.375H8.625V11.125ZM8.625 6.125H7.375V4.875H8.625V6.125Z"
                fill="currentColor"
                className="text-text-secondary"
              />
            </svg>
          )}
        </div>
      </div>
    </a>
  );
}
